import { Link } from 'react-router-dom';

/**
 * /privacy — Jubuddy game (operated by Jubit AI Universe).
 * Adapted from jubit-ai-universe Privacy.tsx with game-specific
 * additions (battle telemetry, fusion blueprints, hero progression).
 */
export function PrivacyPage() {
  return (
    <main className="min-h-screen bg-neutral-950 text-neutral-100">
      <div className="mx-auto max-w-4xl px-6 py-12">
        <Link to="/" className="mb-4 inline-flex items-center text-sm text-neutral-400 hover:text-neutral-100">
          ← Back to Home
        </Link>
        <h1 className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-3xl font-bold text-transparent sm:text-4xl">
          Privacy Policy
        </h1>
        <p className="mt-2 text-sm text-neutral-400">Last updated: September 2026</p>

        <div className="mt-8 space-y-6">
          <Section title="1. Introduction">
            This Privacy Policy describes how <strong>Jubit AI Universe</strong> ("we," "us," or "our") collects,
            uses, and shares information about you through Jubuddy (a tower-defense game accessible at
            jubuddy.com, gghere.com, and via embedded surfaces on jubit.ai). We are committed to protecting
            your privacy in compliance with global standards including GDPR, CCPA, and the Hong Kong Personal
            Data (Privacy) Ordinance (PDPO).
          </Section>

          <Section title="2. Information We Collect">
            <p className="mb-3">We collect information in the following ways:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong>Account information</strong> (when you sign in with Google via Jubit SSO): name, email,
                profile image. We do not store your Google password.
              </li>
              <li>
                <strong>Game state:</strong> hero loadout choices, fusion blueprints, campaign progression, endless-run
                records, audio settings — stored locally in your browser and (when signed in) synced to your account.
              </li>
              <li>
                <strong>Studio content:</strong> any prompts you author in the Studio prompt-builder; generated
                images you choose to save; remix actions on community items.
              </li>
              <li>
                <strong>Usage information:</strong> IP address (anonymized), browser type, device class, pages
                visited, battle outcomes (aggregated for balancing).
              </li>
              <li>
                <strong>Planet play stats (anonymous):</strong> on the planet at gghere.com/planet we record six coarse
                events — session start, a heartbeat, session end, first input, mode start and run finish (scores in
                buckets) — keyed by a random install id stored in your browser, never by your account. No IP address,
                user agent or fingerprint is stored with them. Turn them off any time with the 📊 control in the
                planet's ⋯ rail; browsers that send Global Privacy Control are treated as opted out.
              </li>
              <li>
                <strong>Cookies and similar:</strong> session cookies for authentication; localStorage for game
                state; no third-party advertising trackers.
              </li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Information">
            <ul className="list-disc space-y-2 pl-5">
              <li>Provide, maintain, and improve the game and Studio.</li>
              <li>Sync your loadouts and progression across devices when you sign in.</li>
              <li>Generate images on your behalf via third-party AI providers (see §4).</li>
              <li>Aggregate balance telemetry to tune wave difficulty and fusion weights.</li>
              <li>Communicate service-related notices (e.g., scheduled maintenance).</li>
              <li>Ensure security, prevent fraud, enforce our Terms of Service.</li>
              <li>Comply with legal obligations.</li>
            </ul>
          </Section>

          <Section title="4. How We Share Your Information">
            <p className="mb-3">We do not sell your personal information. We share with:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>
                <strong>Infrastructure providers:</strong> Vercel (hosting), Supabase (auth + database), Cloudflare
                (CDN). They are contractually bound to protect your data.
              </li>
              <li>
                <strong>AI model providers</strong> (when you use Studio image generation): your prompt text is
                sent to providers including <strong>Pollinations, Google (Gemini), OpenAI, xAI (Grok), Alibaba
                (Qwen)</strong>. We do not include your account information in these requests.
              </li>
              <li>
                <strong>Legal requirements:</strong> if compelled by valid legal process.
              </li>
              <li>
                <strong>Business transfers:</strong> in a merger or acquisition.
              </li>
            </ul>
          </Section>

          <Section title="5. Your Data Protection Rights">
            <p className="mb-3">Depending on your location, you may have the right to:</p>
            <ul className="list-disc space-y-2 pl-5">
              <li>Access, update, or delete your personal information.</li>
              <li>Object to or restrict processing.</li>
              <li>Data portability — export your loadouts/blueprints as JSON.</li>
              <li>Withdraw consent.</li>
            </ul>
            <p className="mt-3">
              <strong>Hong Kong residents:</strong> Right to access and correction under PDPO.
            </p>
            <p className="mt-3">
              To exercise: <a className="underline" href="mailto:privacy@jubitai.com">privacy@jubitai.com</a>
            </p>
          </Section>

          <Section title="6. Data Security and Retention">
            <p className="mb-3">
              We use TLS in transit, encryption at rest, and least-privilege access controls. No method is 100% secure.
            </p>
            <p>
              Game state retains while your account is active. Anonymized telemetry, including planet play stats, is
              retained ≤ 24 months for balance and retention analysis.
            </p>
          </Section>

          <Section title="7. International Data Transfers">
            Your information may be transferred outside your jurisdiction (primarily to data centers in Asia, the EU,
            and the US). We rely on standard contractual clauses where required.
          </Section>

          <Section title="8. Children's Privacy">
            Jubuddy is not intended for users under 13. Studio adult-themed content is gated behind a 18+ confirmation
            and is not exposed to users without an adult token.
          </Section>

          <Section title="9. Changes to This Policy">
            We may update this Privacy Policy. Material changes are announced via the home page and the "Last updated"
            timestamp.
          </Section>

          <Section title="10. Contact Us">
            <ul className="list-none space-y-1">
              <li><strong>Privacy:</strong> <a className="underline" href="mailto:privacy@jubitai.com">privacy@jubitai.com</a></li>
              <li><strong>DPO:</strong> <a className="underline" href="mailto:dpo@jubitai.com">dpo@jubitai.com</a></li>
              <li><strong>Address:</strong> Hong Kong SAR</li>
            </ul>
          </Section>
        </div>
      </div>
    </main>
  );
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <section className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6">
      <h2 className="mb-3 text-xl font-semibold sm:text-2xl">{title}</h2>
      <div className="text-sm text-neutral-300 sm:text-base">{children}</div>
    </section>
  );
}

export default PrivacyPage;
